import path from 'node:path';
import { randomUUID } from 'node:crypto';
import util from 'node:util';
import indentString from 'indent-string';
import camelCase from 'lodash/camelCase.js';
import { componentGenerator } from '@wix/gena';
import { getBackendAppToolsExtensionPath } from '@wix/cli-astro-definitions';
import { isStandaloneExtensionsProject } from '@wix/cli-custom-extensions';
import { modifyExtensionsEntry } from '@wix/cli-project-extender';
import { getExtensionRelativePaths } from '../utils/extension-relative-paths.js';

const APP_TOOLS_TEMPLATE = path.resolve(
  __dirname,
  '../templates/astro/app-tools'
);

interface ToolConfig {
  methodName: string;
  description: string;
  requestSchema?: Record<string, unknown>;
  responseSchema?: Record<string, unknown>;
  activated?: boolean;
}

const DEFAULT_TOOL: ToolConfig = {
  methodName: 'myMethod',
  description: 'Description of what this tool does',
  activated: true,
  requestSchema: {
    type: 'object',
    properties: {
      param: { type: 'string' },
    },
    required: ['param'],
  },
  responseSchema: {
    type: 'object',
    properties: {
      result: { type: 'string' },
    },
  },
};

function renderTools(tools: ToolConfig[]): string {
  return `${tools
    .map((tool) =>
      indentString(
        util.inspect(tool, {
          depth: Infinity,
          compact: false,
          breakLength: Infinity,
          maxArrayLength: Infinity,
          maxStringLength: Infinity,
        }),
        4
      )
    )
    .join(',\n')},`;
}

export async function generateAppTools(
  projectFolder: string,
  name: string,
  tools?: ToolConfig[]
) {
  const cwd = getBackendAppToolsExtensionPath(projectFolder, name);
  const { relativePathToConfigFile } = getExtensionRelativePaths({
    cwd,
    projectFolder,
    extensionFileName: name,
    codeFileType: 'ts',
  });

  const isCustomExtensionsProject =
    await isStandaloneExtensionsProject(projectFolder);

  const toolsContent = renderTools(
    tools && tools.length > 0 ? tools : [DEFAULT_TOOL]
  );

  const genResult = await componentGenerator({
    template: APP_TOOLS_TEMPLATE,
    cwd,
    projectFolder,
    data: {
      builderPackage: isCustomExtensionsProject
        ? '@wix/custom-extensions'
        : '@wix/astro/builders',
      name,
      config: { id: randomUUID() },
      toolsContent,
    },
  });

  await modifyExtensionsEntry({
    projectFolder,
    extensionName: camelCase(name),
    relativePath: relativePathToConfigFile,
    isCustomExtensionsProject,
  });

  return genResult;
}
